import axios from "axios"

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:3000")

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken")
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface Shipment {
  id: string
  shipmentNumber: string
  status: string
  customerId: string
  origin: {
    city: string
    state: string
  }
  destination: {
    city: string
    state: string
  }
  serviceLevel: string
  createdAt: string
  updatedAt: string
}

export interface InventoryItem {
  id: string
  sku: string
  locationId: string
  locationType: string
  availableQuantity: number
  reservedQuantity: number
  totalQuantity: number
  unitCost: number
  currency: string
}

export interface TrackingData {
  shipmentId: string
  shipmentNumber: string
  status: string
  currentLocation: {
    latitude: number
    longitude: number
    address: string
    facilityCode?: string
  }
  lastUpdated: string
  estimatedDelivery: string
  carrier: string
  carrierReference: string
}

export interface DashboardData {
  shipments: {
    total: number
    active: number
    delivered: number
    cancelled: number
    inTransit: number
    outForDelivery: number
    averageTransitTime: number
    onTimeDeliveryRate: number
    totalValue: number
    timeRange: string
  }
  inventory: {
    locations: Array<{
      id: string
      name: string
      type: string
      address: string
      capacity: number
      utilization: number
      totalSkus: number
      totalValue: number
    }>
    summary: {
      totalLocations: number
      totalSkus: number
      totalValue: number
      averageUtilization: number
      lowStockAlerts: number
      movementsToday: number
    }
    topSkus: Array<{
      sku: string
      totalQuantity: number
      totalValue: number
      locations: number
    }>
    recentMovements: Array<{
      id: string
      sku: string
      type: string
      quantity: number
      location: string
      fromLocation?: string
      toLocation?: string
      timestamp: string
    }>
  }
  tracking: {
    activeShipments: Array<{
      shipmentId: string
      shipmentNumber: string
      status: string
      currentLocation: {
        latitude: number
        longitude: number
        address: string
        facilityCode: string
      }
      lastUpdated: string
      estimatedDelivery: string
      carrier: string
      carrierReference: string
    }>
    summary: {
      totalActiveShipments: number
      inTransit: number
      outForDelivery: number
      atFacility: number
      deliveredToday: number
      exceptionsToday: number
      onTimeDeliveryRate: number
      averageTransitTime: number
    }
    recentEvents: Array<{
      id: string
      shipmentId: string
      eventType: string
      status: string
      location: string
      timestamp: string
      source: string
    }>
    performanceMetrics: {
      averageLocationUpdateFrequency: number
      dataAccuracy: number
      systemUptime: number
      totalEvents: number
    }
  }
  timestamp: string
}

export const apiService = {
  // Dashboard
  async getDashboardData(): Promise<DashboardData> {
    // In production, use mock data from static files
    if (
      process.env.NODE_ENV === "production" &&
      !process.env.REACT_APP_API_URL
    ) {
      const response = await fetch("/api/analytics/dashboard.json")
      return await response.json()
    }
    const response = await api.get("/api/analytics/dashboard")
    return response.data
  },

  // Shipments
  async getShipments(
    customerId?: string
  ): Promise<{ shipments: Shipment[]; totalCount: number }> {
    // In production, use mock data from static files
    if (
      process.env.NODE_ENV === "production" &&
      !process.env.REACT_APP_API_URL
    ) {
      const response = await fetch("/api/shipments.json")
      return await response.json()
    }
    const params = customerId ? { customerId } : {}
    const response = await api.get("/api/shipments", { params })
    return response.data
  },

  async getShipment(id: string): Promise<Shipment> {
    const response = await api.get(`/api/shipments/${id}`)
    return response.data
  },

  async createShipment(shipmentData: any): Promise<{ shipmentId: string }> {
    const response = await api.post("/api/shipments", shipmentData)
    return response.data
  },

  async optimizeRoute(shipmentId: string, routeData: any): Promise<any> {
    const response = await api.put(
      `/api/shipments/${shipmentId}/optimize-route`,
      routeData
    )
    return response.data
  },

  async assignCarrier(shipmentId: string, carrierData: any): Promise<any> {
    const response = await api.put(
      `/api/shipments/${shipmentId}/assign-carrier`,
      carrierData
    )
    return response.data
  },

  async dispatchShipment(shipmentId: string, dispatchData: any): Promise<any> {
    const response = await api.put(
      `/api/shipments/${shipmentId}/dispatch`,
      dispatchData
    )
    return response.data
  },

  // Inventory
  async getInventory(): Promise<any> {
    // In production, use mock data from static files
    if (
      process.env.NODE_ENV === "production" &&
      !process.env.REACT_APP_API_URL
    ) {
      const response = await fetch("/api/inventory.json")
      return await response.json()
    }
    const response = await api.get("/api/inventory")
    return response.data
  },

  async getInventoryByLocation(locationId: string): Promise<any> {
    const response = await api.get(`/api/inventory/locations/${locationId}`)
    return response.data
  },

  async checkSkuAvailability(
    sku: string,
    requiredQuantity?: number
  ): Promise<any> {
    const params = requiredQuantity ? { requiredQuantity } : {}
    const response = await api.get(`/api/inventory/sku/${sku}/availability`, {
      params,
    })
    return response.data
  },

  async allocateInventory(allocationData: any): Promise<any> {
    const response = await api.post("/api/inventory/allocate", allocationData)
    return response.data
  },

  async reserveInventory(reservationData: any): Promise<any> {
    const response = await api.post("/api/inventory/reserve", reservationData)
    return response.data
  },

  // Tracking
  async getTrackingData(): Promise<any> {
    // In production, use mock data from static files
    if (
      process.env.NODE_ENV === "production" &&
      !process.env.REACT_APP_API_URL
    ) {
      const response = await fetch("/api/tracking.json")
      return await response.json()
    }
    const response = await api.get("/api/tracking")
    return response.data
  },

  async getShipmentTracking(shipmentId: string): Promise<any> {
    const response = await api.get(`/api/tracking/shipments/${shipmentId}`)
    return response.data
  },

  async getActiveShipments(): Promise<any> {
    const response = await api.get("/api/tracking/active-shipments")
    return response.data
  },

  async submitLocationUpdate(updateData: any): Promise<any> {
    const response = await api.post(
      "/api/tracking/location-updates",
      updateData
    )
    return response.data
  },

  async submitStatusUpdate(updateData: any): Promise<any> {
    const response = await api.post("/api/tracking/status-updates", updateData)
    return response.data
  },

  // Analytics
  async getPerformanceMetrics(): Promise<any> {
    const response = await api.get("/api/analytics/performance")
    return response.data
  },

  async getShipmentStats(timeRange?: string): Promise<any> {
    const params = timeRange ? { timeRange } : {}
    const response = await api.get("/api/shipments/stats", { params })
    return response.data
  },

  async getInventoryStats(timeRange?: string): Promise<any> {
    const params = timeRange ? { timeRange } : {}
    const response = await api.get("/api/inventory/stats", { params })
    return response.data
  },

  async getTrackingStats(timeRange?: string): Promise<any> {
    const params = timeRange ? { timeRange } : {}
    const response = await api.get("/api/tracking/stats", { params })
    return response.data
  },
}

export default apiService
